import React, { useMemo } from "react";
import {
  GreeterEvent,
  DaysliceConfig,
  Venue,
  When,
  ImageAsset,
} from "@greeter/core";
import {
  GreeterEventV2Route,
} from "@greeter-guest/utility/Routes";
import { GreenLabel, YellowLabel } from "@greeter/matter";
import { BigContentCard } from "@greeter-guest/components/BigContentCard";
import {
  toPrettyDateString,
  toPrettyTimeString,
} from "@greeter-guest/utility/DateUtil";
import { useIonRouter } from "@ionic/react";
import { useDetectTab } from "@greeter-guest/hooks";

import css from "./BigGreeterEventCard.module.scss";

export type BigGreeterEventCardProps = React.PropsWithChildren & {
  greeterEvent: GreeterEvent;
  venue?: Venue;
  daysliceConfig: DaysliceConfig;
};

export function BigGreeterEventCard({
  greeterEvent,
  venue,
  daysliceConfig,
}: BigGreeterEventCardProps) {
  const { startsAt, endsAt } = greeterEvent;
  const when = useMemo(
    () => GreeterEvent.figureOutWhen(greeterEvent, daysliceConfig.dayOffset),
    [greeterEvent, daysliceConfig.dayOffset]
  );

  const label = useMemo(() => {
    const labels = {
      [When.InProgress]: (
        <GreenLabel
          className={css.Label}
          text={`Igang, slutter kl. ${toPrettyTimeString(endsAt)}`}
        />
      ),
      [When.Today]: (
        <GreenLabel
          className={css.Label}
          text={`Idag, kl. ${toPrettyTimeString(startsAt)}`}
        />
      ),
      [When.Tomorrow]: (
        <YellowLabel
          className={css.Label}
          text={`I morgen, Kl. ${toPrettyTimeString(startsAt)}`}
        />
      ),
    };

    return (
      labels[when] ?? (
        <YellowLabel
          className={css.Label}
          text={`${toPrettyDateString(startsAt)}, kl. ${toPrettyTimeString(
            startsAt
          )}`}
        />
      )
    );
  }, [when, startsAt, endsAt]);

  const uri = ImageAsset.findUriWithSizeOrDefault(
    greeterEvent.coverAsset,
    "16x9-w1024"
  );
  const router = useIonRouter();
  const tab = useDetectTab();

  return (
    <BigContentCard
      onClick={() =>
        router.push(
          new GreeterEventV2Route(tab).route({
            greeterEventId: greeterEvent.id,
          })
        )
      }
      className={css.BigGreeterEventCard}
      title={greeterEvent.name}
      description={venue?.name ?? ""}
      cover={uri}
      venue={venue}
      label={label}
    />
  );
}
